import B2bAPI from '@/API/b2b/b2b-api'
import { B2B_PAYMENT_TYPES } from '@/views/admin/b2b/config/b2b-constants'

const DEFAULT_BUSINESSES_LIMIT = 10

export type B2bBranchAddress = {
  address_label: string
  street: string
  city: string
  block: string
  lat?: number
  long?: number
  house_number?: string
}

export type B2bOperatingHour = {
  weekday: string
  closed: boolean
  time_from: string | null
  time_to: string | null
}

export type B2bBusinessBranch = {
  id: number
  name: string
  address: B2bBranchAddress
  contact_name: string
  contact_number: string
  merchant_branch_id: number
  merchant_branch_name: string
  operating_hours: B2bOperatingHour[] | null
}

export type B2bPaymentMethod = {
  id: number
  method: string
  enabled: boolean
}

export type B2bCreditAccount = {
  credit_limit: number
  outstanding_amount: number
  available_credit: number
  total_settled: number
}

export type B2bBusinessUser = {
  id: number
  name: string
  username: string
  email: string
  phone: string
  status: boolean
  branch_id: number
  branch?: {
    id: number
    name: string
  }
}

export type B2bBusiness = {
  id: number
  name: string
  slug: string
  phone: string
  logo: string
  active: boolean
  status: boolean
  branches_count: number
  items_count: number
  branches: B2bBusinessBranch[]
  payment_methods: B2bPaymentMethod[]
  payment_type: string
  credit_account: B2bCreditAccount
}

function unwrapBusiness(response: any) {
  if (response?.data && !Array.isArray(response.data) && (response.data.id || response.data.name)) {
    return response.data
  }
  if (response?.business) return response.business
  return response
}

function unwrapBusinesses(response: any) {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response?.businesses)) return response.businesses
  return []
}

function unwrapList(response: any, keys: string[]) {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.data)) return response.data
  for (let i = 0; i < keys.length; i += 1) {
    if (Array.isArray(response?.[keys[i]])) return response[keys[i]]
    if (Array.isArray(response?.data?.[keys[i]])) return response.data[keys[i]]
  }
  return []
}

function toNumber(value: any, fallback = 0) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function hasCreditValue(value: any) {
  return value != null && value !== ''
}

function creditValue(businessValue: any, accountValue: any) {
  return hasCreditValue(businessValue) ? businessValue : accountValue
}

function normalizeCreditAccount(account: any = {}, business: any = {}): B2bCreditAccount {
  const creditLimit = toNumber(creditValue(business.credit_limit, account.credit_limit))
  const outstandingAmount = toNumber(creditValue(business.outstanding_amount, account.outstanding_amount))
  const totalSettled = toNumber(creditValue(business.total_settled, account.total_settled))
  const availableSource = creditValue(business.available_credit, account.available_credit)
  const availableCredit = hasCreditValue(availableSource)
    ? toNumber(availableSource)
    : creditLimit - outstandingAmount
  return {
    credit_limit: creditLimit,
    outstanding_amount: outstandingAmount,
    available_credit: availableCredit,
    total_settled: totalSettled,
  }
}

function normalizeOperatingHours(hours: any): B2bOperatingHour[] | null {
  if (!Array.isArray(hours)) return null
  return hours.reduce((acc: B2bOperatingHour[], item: any) => {
    const weekday = String(item?.weekday || item?.day || '').toLowerCase()
    if (!weekday) return acc
    acc.push({
      weekday,
      closed: item.closed === true || item.closed === 1 || item.is_closed === true,
      time_from: item.time_from || item.from || null,
      time_to: item.time_to || item.to || null,
    })
    return acc
  }, [])
}

function toOperatingHoursPayload(hours: any): B2bOperatingHour[] | null {
  return normalizeOperatingHours(hours)
}

function normalizePaymentMethod(method: any): B2bPaymentMethod {
  return {
    id: Number(method.id),
    method: method.method || '',
    enabled: method.enabled !== false && method.enabled !== 0,
  }
}

function normalizePaymentMethods(methods: any): B2bPaymentMethod[] {
  if (!Array.isArray(methods)) return []
  return methods.map(normalizePaymentMethod)
}

function toAddressPayload(address: any = {}): B2bBranchAddress {
  const houseNumber = String(address?.house_number ?? '').trim()
  return {
    address_label: String(address?.address_label ?? '').trim(),
    street: String(address?.street ?? '').trim(),
    city: String(address?.city ?? '').trim(),
    block: String(address?.block ?? '').trim(),
    lat: Number(address?.lat),
    long: Number(address?.long),
    ...(houseNumber ? { house_number: houseNumber } : {}),
  }
}

function normalizeAddress(branch: any): B2bBranchAddress {
  const address = branch?.address && typeof branch.address === 'object'
    ? branch.address
    : branch || {}
  return toAddressPayload(address)
}

function normalizeBranch(branch: any): B2bBusinessBranch {
  return {
    id: Number(branch.id),
    name: branch.name || '',
    address: normalizeAddress(branch),
    contact_name: branch.contact_name || '',
    contact_number: branch.contact_number || '',
    merchant_branch_id: Number(branch.merchant_branch_id) || 0,
    merchant_branch_name: branch.merchant_branch_name || branch.merchant_branch?.name || '',
    operating_hours: normalizeOperatingHours(
      Object.prototype.hasOwnProperty.call(branch, 'operating_hours')
        ? branch.operating_hours
        : branch.opening_hours,
    ),
  }
}

function isActive(business: any) {
  return !!(business?.status ?? business?.active ?? business?.is_active)
}

function normalizeLogo(logo: any): string {
  return typeof logo === 'string' ? logo : ''
}

function normalizePaymentType(value: any) {
  return value === B2B_PAYMENT_TYPES.CREDIT
    ? B2B_PAYMENT_TYPES.CREDIT
    : B2B_PAYMENT_TYPES.DIRECT_PAYMENT
}

function normalizeBusiness(business: any): B2bBusiness {
  const branches = Array.isArray(business?.branches) ? business.branches.map(normalizeBranch) : []
  const status = isActive(business)
  return {
    id: Number(business.id),
    name: business.name || '',
    slug: business.slug || '',
    phone: business.phone || '',
    logo: normalizeLogo(business.logo ?? business.logo_url),
    active: status,
    status,
    items_count: business.items_count || 0,
    branches,
    branches_count: business.branches_count ?? branches.length,
    payment_methods: normalizePaymentMethods(business.payment_methods),
    payment_type: normalizePaymentType(business.payment_type),
    credit_account: normalizeCreditAccount(business.credit_account || {}, business),
  }
}

type BusinessWritePayload = Omit<Partial<B2bBusiness>, 'logo' | 'credit_account'> & {
  logo?: string | Blob | null
  remove_logo?: 0 | 1 | boolean
  credit_limit?: number
}

function toBusinessPayload(payload: BusinessWritePayload, existing?: B2bBusiness | null) {
  const slug = (payload.slug ?? existing?.slug ?? '').trim().toLowerCase()
  const status = isActive({ ...existing, ...payload })
  const paymentMethods = Array.isArray(payload.payment_methods)
    ? normalizePaymentMethods(payload.payment_methods)
    : normalizePaymentMethods(existing?.payment_methods)
  const paymentType = normalizePaymentType(payload.payment_type ?? existing?.payment_type)
  const creditLimit = payload.credit_limit != null
    ? toNumber(payload.credit_limit)
    : toNumber(existing?.credit_account?.credit_limit)

  const body: Record<string, any> = {
    name: (payload.name ?? existing?.name ?? '').trim(),
    slug,
    phone: payload.phone ?? existing?.phone ?? '',
    status,
    payment_methods: paymentMethods,
    payment_type: paymentType,
  }

  if (paymentType === B2B_PAYMENT_TYPES.CREDIT && !existing) {
    body.credit_limit = creditLimit
  }

  if (payload.logo instanceof Blob) {
    body.logo = payload.logo
  } else if (payload.remove_logo === 1 || payload.remove_logo === true) {
    body.remove_logo = 1
  }

  return body
}

function toBranchBody(payload: Partial<B2bBusinessBranch>, existing?: B2bBusinessBranch | null) {
  return {
    name: (payload.name ?? existing?.name ?? '').trim(),
    address: toAddressPayload(payload.address ?? existing?.address),
    contact_name: (payload.contact_name ?? existing?.contact_name ?? '').trim(),
    contact_number: payload.contact_number ?? existing?.contact_number ?? '',
    merchant_branch_id: payload.merchant_branch_id ?? existing?.merchant_branch_id,
    operating_hours: toOperatingHoursPayload(
      payload.operating_hours !== undefined ? payload.operating_hours : existing?.operating_hours,
    ),
  }
}

export default {
  namespaced: true,
  state: {
    businesses: [] as B2bBusiness[],
    businessesLimit: DEFAULT_BUSINESSES_LIMIT,
  },
  getters: {
    businesses: (state: any) => state.businesses,
    businessesLimit: (state: any) => state.businessesLimit,
    businessesCount: (state: any) => state.businesses.length,
    canCreateBusiness: (state: any) => state.businesses.length < state.businessesLimit,
    getBusinessById: (state: any) => (id: number) => state.businesses.find((business: B2bBusiness) => Number(business.id) === Number(id)) ?? null,
    getBusinessBySlug: (state: any) => (slug: string) => state.businesses.find((business: B2bBusiness) => business.slug === slug) ?? null,
    getBusinessBranches: (state: any, getters: any) => (businessId: number) => getters.getBusinessById(businessId)?.branches ?? [],
    getBusinessBranchById: (state: any, getters: any) => (businessId: number, branchId: number) => (
      getters.getBusinessBranches(businessId).find((branch: B2bBusinessBranch) => branch.id === branchId) ?? null
    ),
    getMappedMerchantBranchId: (state: any, getters: any) => (businessId: number, branchId: number) => (
      getters.getBusinessBranchById(businessId, branchId)?.merchant_branch_id ?? null
    ),
    isSlugTaken: (state: any) => (slug: string, excludeId: number | null = null) => state.businesses.some(
      (business: B2bBusiness) => business.slug === slug && Number(business.id) !== Number(excludeId),
    ),
    canAccessOrderingPage: (state: any, getters: any) => (slug: string) => {
      const business = getters.getBusinessBySlug(slug)
      return isActive(business)
    },
    businessSelectOptions: (state: any) => state.businesses.map((business: B2bBusiness) => ({
      id: business.id,
      name: business.name,
    })),
  },
  mutations: {
    SET_BUSINESSES(state: any, businesses: B2bBusiness[]) {
      state.businesses = businesses.map(normalizeBusiness)
    },
    UPSERT_BUSINESS(state: any, business: B2bBusiness) {
      const normalized = normalizeBusiness(business)
      const index = state.businesses.findIndex((item: B2bBusiness) => Number(item.id) === Number(normalized.id))
      if (index === -1) {
        state.businesses = [...state.businesses, normalized]
      } else {
        const businesses = [...state.businesses]
        businesses.splice(index, 1, {
          ...businesses[index],
          ...normalized,
          branches: normalized.branches.length ? normalized.branches : businesses[index].branches,
        })
        state.businesses = businesses
      }
    },
    DELETE_BUSINESS(state: any, id: number) {
      state.businesses = state.businesses.filter((business: B2bBusiness) => Number(business.id) !== Number(id))
    },
  },
  actions: {
    async FETCH_BUSINESSES({ commit }: any) {
      const response = await B2bAPI.getBusinesses()
      const businesses = unwrapBusinesses(response).map(normalizeBusiness)
      commit('SET_BUSINESSES', businesses)
      return businesses
    },
    async CREATE_BUSINESS({ getters, commit }: any, payload: BusinessWritePayload) {
      if (!getters.canCreateBusiness) {
        throw new Error('business-limit-reached')
      }

      const body = toBusinessPayload(payload)
      if (!body.slug) throw new Error('slug-required')
      if (getters.isSlugTaken(body.slug)) throw new Error('slug-taken')

      const response = await B2bAPI.create(body)
      const business = normalizeBusiness(unwrapBusiness(response))
      commit('UPSERT_BUSINESS', business)
      return business
    },
    async UPDATE_BUSINESS({ getters, commit }: any, payload: BusinessWritePayload & { id: number }) {
      const existing = getters.getBusinessById(payload.id)
      const body = toBusinessPayload(payload, existing)
      if (!body.slug) throw new Error('slug-required')
      if (getters.isSlugTaken(body.slug, payload.id)) throw new Error('slug-taken')

      const response = await B2bAPI.update(payload.id, body)
      const responseBusiness = unwrapBusiness(response)
      const next = {
        ...(existing || payload),
        ...responseBusiness,
        id: payload.id,
      }

      if (payload.remove_logo === 1 || payload.remove_logo === true) {
        next.logo = typeof responseBusiness?.logo === 'string' ? responseBusiness.logo : ''
      } else if (typeof next.logo !== 'string') {
        next.logo = existing?.logo || ''
      }

      const business = normalizeBusiness(next)
      commit('UPSERT_BUSINESS', business)
      return business
    },
    async DELETE_BUSINESS({ commit }: any, id: number) {
      await B2bAPI.delete(id)
      commit('DELETE_BUSINESS', id)
    },
    async FETCH_BRANCHES({ getters, commit }: any, businessId: number) {
      const response = await B2bAPI.getBranches(businessId)
      const branches = unwrapList(response, ['branches']).map(normalizeBranch)
      const business = getters.getBusinessById(businessId)
      commit('UPSERT_BUSINESS', {
        ...(business || { id: businessId }),
        branches,
        branches_count: branches.length,
      })
      return branches
    },
    async CREATE_BRANCH(_: any, payload: { businessId: number } & Partial<B2bBusinessBranch>) {
      if (!payload.merchant_branch_id) throw new Error('merchant-branch-required')

      const body = toBranchBody(payload)
      if (!body.name) throw new Error('branch-name-required')

      await B2bAPI.createBranch(payload.businessId, body)
    },
    async UPDATE_BRANCH({ getters }: any, payload: { businessId: number; id: number } & Partial<B2bBusinessBranch>) {
      const existing = getters.getBusinessBranchById(payload.businessId, payload.id)
      const body = toBranchBody(payload, existing)
      if (!body.merchant_branch_id) throw new Error('merchant-branch-required')
      if (!body.name) throw new Error('branch-name-required')

      await B2bAPI.updateBranch(payload.businessId, payload.id, body)
    },
    async DELETE_BRANCH(_: any, payload: { businessId: number; id: number }) {
      await B2bAPI.deleteBranch(payload.businessId, payload.id)
    },
    async FETCH_CREDIT_ACCOUNT({ getters, commit }: any, businessId: number) {
      const response = await B2bAPI.getBusiness(businessId)
      const existing = getters.getBusinessById(businessId) || { id: businessId }
      const business = normalizeBusiness({
        ...existing,
        ...unwrapBusiness(response),
        id: businessId,
      })
      commit('UPSERT_BUSINESS', business)
      return business.credit_account
    },
    async SETTLE_CREDIT(_: any, payload: { businessId: number; amount: number; settlement_date: string; payment_reference: string; note?: string }) {
      const amount = Number(payload.amount)
      if (!(amount > 0)) throw new Error('invalid-settlement-amount')
      await B2bAPI.settleCredit(payload.businessId, {
        amount,
        settlement_date: payload.settlement_date,
        payment_reference: payload.payment_reference,
        note: payload.note || '',
      })
    },
    async ADJUST_CREDIT_LIMIT(_: any, payload: { businessId: number; change_type: string; credit_limit: number; note?: string }) {
      const creditLimit = Number(payload.credit_limit)
      if (!Number.isFinite(creditLimit) || creditLimit < 0) throw new Error('invalid-credit-limit-change')
      await B2bAPI.adjustCreditLimit(payload.businessId, {
        change_type: payload.change_type,
        credit_limit: creditLimit,
        note: payload.note || '',
      })
    },
    async FETCH_SETTLEMENT_HISTORY(_: any, businessId: number) {
      const response = await B2bAPI.getSettlementHistory(businessId)
      return unwrapList(response, ['settlements', 'history', 'rows', 'items'])
    },
    async FETCH_CREDIT_LIMIT_HISTORY(_: any, businessId: number) {
      const response = await B2bAPI.getCreditLimitHistory(businessId)
      const list = unwrapList(response, ['history', 'limit_history', 'credit', 'rows'])
      if (list.length) return list
      const record = response?.data && !Array.isArray(response.data) ? response.data : response
      if (record && (record.previous_limit != null || record.new_limit != null || record.change_type != null)) {
        return [record]
      }
      return []
    },
    async CREATE_USER(_: any, payload: { businessId: number } & Record<string, any>) {
      const { businessId, ...body } = payload
      return B2bAPI.createUser(businessId, body)
    },
    async UPDATE_USER(_: any, payload: { businessId: number; id: number } & Record<string, any>) {
      const { businessId, id, ...body } = payload
      return B2bAPI.updateUser(businessId, id, body)
    },
    async DELETE_USER(_: any, payload: { businessId: number; id: number }) {
      return B2bAPI.deleteUser(payload.businessId, payload.id)
    },
    async RESET_USER_CREDENTIALS(_: any, payload: { businessId: number; id: number; password: string }) {
      return B2bAPI.resetUserCredentials(payload.businessId, payload.id, { password: payload.password })
    },
  },
}
